import {
  DEFAULT_MAIL_EDITOR_LINK_HREF,
  escapeHtmlAttr,
  escapeHtmlPlainSegment,
  mailCtaButtonLinkOpenClose,
  MAIL_EDITOR_PH_CHIP_CLASS,
  MAIL_EDITOR_PH_CHIP_MARKER,
  MAIL_EDITOR_PH_HREF_ATTR,
  MAIL_EDITOR_PH_KEY_ATTR,
  MAIL_EDITOR_PH_STYLE_ATTR,
  mailPhEditorChipHtml,
  mailPhToken,
} from "./mailLineParts";
import type { MailPhStyle } from "./mailLineParts";
import type { MailPhHrefScheme } from "./mailPlaceholderContext";

function readMailPhStyleFromChipElement(chip: HTMLElement): MailPhStyle {
  const v = chip.getAttribute(MAIL_EDITOR_PH_STYLE_ATTR);

  if (v === "bold" || v === "italic" || v === "highlight" || v === "none") {
    return v;
  }

  return "none";
}

function isMailPhChipElement(el: HTMLElement): boolean {
  return (
    el.classList.contains(MAIL_EDITOR_PH_CHIP_CLASS) &&
    el.getAttribute(MAIL_EDITOR_PH_CHIP_MARKER) === "1"
  );
}

// contenteditable="false" chips: select the node on primary mousedown so the caret does not skip past.
export function trySelectMailPhChipFromPointerEvent(
  editor: HTMLDivElement,
  event: { target: EventTarget | null; preventDefault: () => void; button?: number },
): boolean {
  if (event.button !== undefined && event.button !== 0) {
    return false;
  }

  const t = event.target;

  if (!t || !(t instanceof HTMLElement)) {
    return false;
  }

  const chip = t.closest(`[${MAIL_EDITOR_PH_CHIP_MARKER}="1"]`) as HTMLElement | null;

  if (!chip || !isMailPhChipElement(chip) || !editor.contains(chip)) {
    return false;
  }

  event.preventDefault();

  editor.focus();

  const sel = window.getSelection();

  if (!sel) {
    return true;
  }

  const range = document.createRange();
  range.selectNode(chip);
  sel.removeAllRanges();
  sel.addRange(range);

  return true;
}

function isIgnorableMailEditorBoundaryNode(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? "") === "";
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === "br") {
    return true;
  }

  return (
    tag === "span" &&
    el.getAttribute(MAIL_EDITOR_PH_CHIP_MARKER) !== "1" &&
    (el.textContent ?? "").trim() === ""
  );
}

function walkMailEditorSibling(node: Node | null, dir: "prev" | "next"): Node | null {
  let n = node;

  while (n && isIgnorableMailEditorBoundaryNode(n)) {
    n = dir === "prev" ? n.previousSibling : n.nextSibling;
  }

  return n;
}

function mailPhChipFromNode(node: Node | null, editor: HTMLElement): HTMLElement | null {
  if (!node || !editor.contains(node)) {
    return null;
  }

  if (node instanceof HTMLElement && isMailPhChipElement(node)) {
    return node;
  }

  return null;
}

function mailPhChipBeforeCollapsedRange(editor: HTMLDivElement, range: Range): HTMLElement | null {
  const { startContainer, startOffset } = range;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    if (startOffset > 0) {
      return null;
    }

    return mailPhChipFromNode(
      walkMailEditorSibling(startContainer.previousSibling, "prev"),
      editor,
    );
  }

  if (startContainer.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  if (startOffset > 0) {
    const prev = walkMailEditorSibling(startContainer.childNodes[startOffset - 1] ?? null, "prev");

    return mailPhChipFromNode(prev, editor);
  }

  if (startContainer === editor) {
    return null;
  }

  return mailPhChipFromNode(walkMailEditorSibling(startContainer.previousSibling, "prev"), editor);
}

function mailPhChipAfterCollapsedRange(editor: HTMLDivElement, range: Range): HTMLElement | null {
  const { startContainer, startOffset } = range;

  if (startContainer.nodeType === Node.TEXT_NODE) {
    const text = startContainer.textContent ?? "";

    if (startOffset < text.length) {
      return null;
    }

    return mailPhChipFromNode(walkMailEditorSibling(startContainer.nextSibling, "next"), editor);
  }

  if (startContainer.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const next = walkMailEditorSibling(startContainer.childNodes[startOffset] ?? null, "next");

  return mailPhChipFromNode(next, editor);
}

function removeMailPhChipLeavingCaret(editor: HTMLDivElement, chip: HTMLElement): boolean {
  const parent = chip.parentNode;

  if (!parent) {
    return false;
  }

  const caret = document.createTextNode("");
  parent.insertBefore(caret, chip);
  chip.remove();
  editor.focus();

  const sel = window.getSelection();

  if (sel) {
    const range = document.createRange();
    range.setStart(caret, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  return true;
}

export function tryDeleteMailPhChipFromKeyboard(
  editor: HTMLDivElement,
  key: "Backspace" | "Delete",
): boolean {
  const sel = window.getSelection();

  if (!sel || sel.rangeCount === 0 || !sel.anchorNode || !editor.contains(sel.anchorNode)) {
    return false;
  }

  const selectedChip = findMailPhChipAtSelection(editor);

  if (selectedChip) {
    const onChip =
      !sel.isCollapsed || sel.anchorNode === selectedChip || selectedChip.contains(sel.anchorNode);

    if (onChip) {
      return removeMailPhChipLeavingCaret(editor, selectedChip);
    }
  }

  if (!sel.isCollapsed) {
    return false;
  }

  const range = sel.getRangeAt(0);
  const chip =
    key === "Backspace"
      ? mailPhChipBeforeCollapsedRange(editor, range)
      : mailPhChipAfterCollapsedRange(editor, range);

  if (!chip) {
    return false;
  }

  return removeMailPhChipLeavingCaret(editor, chip);
}

export function findMailPhChipAtSelection(editor: HTMLDivElement): HTMLElement | null {
  const sel = window.getSelection();

  if (!sel) {
    return null;
  }

  if (sel.rangeCount > 0) {
    const r = sel.getRangeAt(0);

    if (
      r.startContainer === r.endContainer &&
      r.startContainer.nodeType === Node.ELEMENT_NODE &&
      editor.contains(r.startContainer)
    ) {
      const parent = r.startContainer as HTMLElement;

      if (r.endOffset === r.startOffset + 1) {
        const n = parent.childNodes[r.startOffset];

        if (n instanceof HTMLElement && isMailPhChipElement(n) && editor.contains(n)) {
          return n;
        }
      }
    }
  }

  if (sel.anchorNode) {
    let n: Node | null = sel.anchorNode;

    if (n.nodeType === Node.TEXT_NODE) {
      n = n.parentElement;
    }

    while (n && n !== editor) {
      if (n instanceof HTMLElement && isMailPhChipElement(n)) {
        return n;
      }

      n = n.parentElement;
    }
  }

  if (sel.rangeCount > 0) {
    const chips = editor.querySelectorAll<HTMLElement>(`[${MAIL_EDITOR_PH_CHIP_MARKER}="1"]`);

    for (const chip of chips) {
      if (!chip.classList.contains(MAIL_EDITOR_PH_CHIP_CLASS)) {
        continue;
      }

      try {
        if (sel.containsNode(chip, true)) {
          return chip;
        }
      } catch {
        // Some engines may throw on containsNode with detached nodes.
      }
    }
  }

  return null;
}

// Applying the current style again clears it to "none".
export function toggleMailPhChipStyleAtSelection(
  editor: HTMLDivElement,
  style: MailPhStyle,
): boolean {
  const chip = findMailPhChipAtSelection(editor);

  if (!chip || !editor.contains(chip)) {
    return false;
  }

  const keyRaw = chip.getAttribute(MAIL_EDITOR_PH_KEY_ATTR)?.trim() ?? "";

  if (!/^[A-Za-z0-9_]+$/.test(keyRaw)) {
    return false;
  }

  const cur = readMailPhStyleFromChipElement(chip);
  const next: MailPhStyle = cur === style ? "none" : style;
  const visibleLabel = (chip.textContent ?? "").trim() || undefined;
  const hrefRaw = chip.getAttribute(MAIL_EDITOR_PH_HREF_ATTR);
  const hrefScheme = hrefRaw === "mailto" || hrefRaw === "tel" ? hrefRaw : undefined;
  const holder = document.createElement("div");
  holder.innerHTML = mailPhEditorChipHtml(keyRaw, next, visibleLabel, hrefScheme);
  const fresh = holder.firstElementChild as HTMLElement | null;

  if (!fresh) {
    return false;
  }

  chip.replaceWith(fresh);
  editor.focus();

  const afterSel = window.getSelection();

  if (afterSel) {
    const range = document.createRange();
    range.selectNodeContents(fresh);
    range.collapse(false);
    afterSel.removeAllRanges();
    afterSel.addRange(range);
  }

  return true;
}

export function applyMailLinkReplacingPhChipAtSelection(
  editor: HTMLDivElement,
  hrefRaw: string,
): boolean {
  const chip = findMailPhChipAtSelection(editor);

  if (!chip || !editor.contains(chip)) {
    return false;
  }

  const keyRaw = chip.getAttribute(MAIL_EDITOR_PH_KEY_ATTR)?.trim() ?? "";

  if (!/^[A-Za-z0-9_]+$/.test(keyRaw)) {
    return false;
  }

  const href = hrefRaw.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;
  const token = escapeHtmlPlainSegment(mailPhToken(keyRaw));
  const html = `<a href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer">${token}</a>`;
  const holder = document.createElement("div");
  holder.innerHTML = html;
  const linkEl = holder.firstElementChild as HTMLElement | null;

  if (!linkEl) {
    return false;
  }

  chip.replaceWith(linkEl);
  editor.focus();

  const range = document.createRange();
  range.selectNodeContents(linkEl);
  range.collapse(false);
  const sel = window.getSelection();

  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }

  return true;
}

export function findMailAnchorAtSelection(editor: HTMLDivElement): HTMLAnchorElement | null {
  const sel = window.getSelection();

  if (!sel || sel.rangeCount === 0 || !isSelectionInsideContentEditable(editor)) {
    return null;
  }

  const node = sel.anchorNode;

  if (!node || !editor.contains(node)) {
    return null;
  }

  const startEl = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
  const anchor = startEl?.closest("a");

  if (anchor instanceof HTMLAnchorElement && editor.contains(anchor)) {
    return anchor;
  }

  return null;
}

export function applyMailAnchorHrefAtSelection(editor: HTMLDivElement, hrefRaw: string): boolean {
  const a = findMailAnchorAtSelection(editor);

  if (!a || !editor.contains(a)) {
    return false;
  }

  const href = hrefRaw.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;

  a.setAttribute("href", href);
  editor.focus();

  return true;
}

export function applyMailButtonReplacingPhChipAtSelection(
  editor: HTMLDivElement,
  hrefRaw: string = DEFAULT_MAIL_EDITOR_LINK_HREF,
  chipOverride?: HTMLElement | null,
): boolean {
  const chip =
    chipOverride &&
    editor.contains(chipOverride) &&
    isMailPhChipElement(chipOverride as HTMLElement)
      ? (chipOverride as HTMLElement)
      : findMailPhChipAtSelection(editor);

  if (!chip || !editor.contains(chip)) {
    return false;
  }

  const keyRaw = chip.getAttribute(MAIL_EDITOR_PH_KEY_ATTR)?.trim() ?? "";

  if (!/^[A-Za-z0-9_]+$/.test(keyRaw)) {
    return false;
  }

  const href = hrefRaw.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;
  const { open, close } = mailCtaButtonLinkOpenClose(href);
  const inner = escapeHtmlPlainSegment(mailPhToken(keyRaw));
  const holder = document.createElement("div");
  holder.innerHTML = `${open}${inner}${close}`;
  const root = holder.firstElementChild as HTMLElement | null;

  if (!root) {
    return false;
  }

  chip.replaceWith(root);
  editor.focus();

  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  const sel = window.getSelection();

  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }

  return true;
}

export function hrefAndInnerFromAnchorSelectionHtml(selHtml: string): {
  href: string;
  mid: string;
} {
  const trimmed = selHtml.trim();
  const m = trimmed.match(/^<a\s[^>]*\bhref="([^"]*)"[^>]*>([\s\S]*)<\/a>$/i);

  if (m) {
    return { href: m[1], mid: m[2].length > 0 ? m[2] : "" };
  }

  return {
    href: DEFAULT_MAIL_EDITOR_LINK_HREF,
    mid: trimmed.length > 0 ? trimmed : "Action",
  };
}

export function isSelectionInsideContentEditable(editor: HTMLDivElement): boolean {
  const sel = window.getSelection();

  if (!sel || sel.rangeCount === 0) {
    return false;
  }

  return editor.contains(sel.anchorNode);
}

export function collapseCaretToEnd(editor: HTMLDivElement): void {
  editor.focus();

  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  const sel = window.getSelection();

  if (!sel) {
    return;
  }

  sel.removeAllRanges();
  sel.addRange(range);
}

export function getSelectionInnerHtml(editor: HTMLDivElement): string {
  const sel = window.getSelection();

  if (!sel || sel.rangeCount === 0 || !isSelectionInsideContentEditable(editor)) {
    return "";
  }

  const range = sel.getRangeAt(0);
  const holder = document.createElement("div");
  holder.appendChild(range.cloneContents());

  return holder.innerHTML;
}

export function insertMailPhChipAtCaretInContentEditable(
  editor: HTMLDivElement,
  key: string,
  style: MailPhStyle = "none",
  visibleLabel?: string,
  hrefScheme?: MailPhHrefScheme,
): void {
  const html = mailPhEditorChipHtml(key, style, visibleLabel, hrefScheme);
  const holder = document.createElement("div");

  holder.innerHTML = html;

  const node = holder.firstChild;

  if (!node) {
    return;
  }

  editor.focus();

  const sel = window.getSelection();
  let range: Range | null = null;

  if (sel && sel.rangeCount > 0 && isSelectionInsideContentEditable(editor)) {
    range = sel.getRangeAt(0);
  } else {
    collapseCaretToEnd(editor);

    const nextSel = window.getSelection();

    if (nextSel && nextSel.rangeCount > 0) {
      range = nextSel.getRangeAt(0);
    }
  }

  if (!range) {
    editor.appendChild(node);

    return;
  }

  range.deleteContents();
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);

  const liveSel = window.getSelection();

  liveSel?.removeAllRanges();
  liveSel?.addRange(range);
}

export function insertPlainTextAtCaretInContentEditable(
  editor: HTMLDivElement,
  plain: string,
): void {
  editor.focus();

  const sel = window.getSelection();
  let range: Range;

  if (!sel || sel.rangeCount === 0 || !isSelectionInsideContentEditable(editor)) {
    collapseCaretToEnd(editor);
    const nextSel = window.getSelection();

    if (!nextSel || nextSel.rangeCount === 0) {
      editor.appendChild(document.createTextNode(plain));

      return;
    }

    range = nextSel.getRangeAt(0);
  } else {
    range = sel.getRangeAt(0);
  }

  range.deleteContents();
  const tn = document.createTextNode(plain);
  range.insertNode(tn);
  range.setStartAfter(tn);
  range.collapse(true);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

export function execInsertHtmlInMailEditor(editor: HTMLDivElement, html: string): void {
  editor.focus();

  if (!isSelectionInsideContentEditable(editor)) {
    collapseCaretToEnd(editor);
  }

  document.execCommand("insertHTML", false, html);
}

export function execBoldInMailEditor(editor: HTMLDivElement): void {
  editor.focus();

  if (!isSelectionInsideContentEditable(editor)) {
    collapseCaretToEnd(editor);
  }

  document.execCommand("bold", false);
}

export function execItalicInMailEditor(editor: HTMLDivElement): void {
  editor.focus();

  if (!isSelectionInsideContentEditable(editor)) {
    collapseCaretToEnd(editor);
  }

  document.execCommand("italic", false);
}

export function applyMailLinkAtSelection(editor: HTMLDivElement, hrefRaw: string): void {
  if (applyMailAnchorHrefAtSelection(editor, hrefRaw)) {
    return;
  }

  const href = hrefRaw.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;
  const mid = getSelectionInnerHtml(editor);
  const inner = mid.length > 0 ? mid : "";
  const open = `<a href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer">`;
  execInsertHtmlInMailEditor(editor, `${open}${inner}</a>`);
}

export function applyMailButtonLinkAtSelection(editor: HTMLDivElement): void {
  const selHtml = getSelectionInnerHtml(editor);
  const { href, mid } = hrefAndInnerFromAnchorSelectionHtml(selHtml);
  const { open, close } = mailCtaButtonLinkOpenClose(href);
  execInsertHtmlInMailEditor(editor, `${open}${mid}${close}`);
}
